import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const HeatMap = ({ data }) => {
  if (!data || !data.hourly) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Mapa de Calor - Conversas por Horário</h3>
        <p className="text-gray-500">Nenhum dado disponível</p>
      </div>
    );
  }

  const { hourly, labels, maxValue } = data;

  // Preparar dados para o gráfico
  const chartData = hourly.map((count, index) => ({
    hour: labels.hours[index],
    count: count,
    hourIndex: index
  }));

  // Função para definir cor baseada na intensidade
  const getColor = (value) => {
    if (value === 0) return '#e5e7eb'; // gray-200
    const intensity = value / maxValue;
    if (intensity > 0.75) return '#ef4444'; // red-500
    if (intensity > 0.5) return '#f97316'; // orange-500
    if (intensity > 0.25) return '#eab308'; // yellow-500
    return '#22c55e'; // green-500
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold mb-4">Conversas por Hora do Dia</h3>

      <ResponsiveContainer width="100%" height={400}>
        <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="hour"
            angle={-45}
            textAnchor="end"
            height={80}
            interval={0}
            style={{ fontSize: '12px' }}
          />
          <YAxis
            label={{ value: 'Número de Conversas', angle: -90, position: 'insideLeft' }}
          />
          <Tooltip
            content={({ active, payload }) => {
              if (active && payload && payload.length) {
                return (
                  <div className="bg-white p-3 border border-gray-200 rounded shadow-lg">
                    <p className="font-semibold">{payload[0].payload.hour}</p>
                    <p className="text-sm text-gray-600">
                      Conversas: <span className="font-bold">{payload[0].value}</span>
                    </p>
                  </div>
                );
              }
              return null;
            }}
          />
          <Bar dataKey="count">
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={getColor(entry.count)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* Estatísticas resumidas */}
      <div className="mt-6 grid grid-cols-3 gap-4">
        <div className="bg-blue-50 p-4 rounded-lg">
          <p className="text-sm text-gray-600">Total de Conversas</p>
          <p className="text-2xl font-bold text-blue-600">
            {hourly.reduce((sum, count) => sum + count, 0)}
          </p>
        </div>

        <div className="bg-green-50 p-4 rounded-lg">
          <p className="text-sm text-gray-600">Horário de Pico</p>
          <p className="text-2xl font-bold text-green-600">
            {labels.hours[hourly.indexOf(Math.max(...hourly))]}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {Math.max(...hourly)} conversas
          </p>
        </div>

        <div className="bg-orange-50 p-4 rounded-lg">
          <p className="text-sm text-gray-600">Média por Hora</p>
          <p className="text-2xl font-bold text-orange-600">
            {(hourly.reduce((sum, count) => sum + count, 0) / hourly.filter(h => h > 0).length).toFixed(1)}
          </p>
        </div>
      </div>

      {/* Legenda de cores */}
      <div className="mt-4 flex items-center justify-center space-x-4 text-sm">
        <span className="text-gray-600">Intensidade:</span>
        <div className="flex items-center space-x-2">
          <div className="flex items-center space-x-1">
            <div className="w-6 h-6 bg-gray-200 border border-gray-300"></div>
            <span className="text-xs">Baixa</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-6 h-6 bg-green-500 border border-gray-300"></div>
            <span className="text-xs">Média</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-6 h-6 bg-yellow-500 border border-gray-300"></div>
            <span className="text-xs">Alta</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-6 h-6 bg-orange-500 border border-gray-300"></div>
            <span className="text-xs">Muito Alta</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-6 h-6 bg-red-500 border border-gray-300"></div>
            <span className="text-xs">Crítica</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeatMap;
